/**
 * پایش خطای سمت کلاینت — فقط از طریق endpoint داخلی، بدون هیچ سرویس بیرونی.
 *
 * دو حالت:
 *   ۱) کاربر واردشده  → POST /api/LogEvent/Client   بدنه: { State:int, Message:string }
 *      توکن کاربر توسط interceptor اضافه می‌شود؛ سرور پروژه و UserId را خودش تشخیص می‌دهد.
 *      هیچ مقدار حساسی در باندل نیست.
 *   ۲) کاربر واردنشده + VUE_APP_LOGEVENT_KEY تنظیم‌شده → POST /api/LogEvent با { State, Key, Message }
 *      این Key در باندل قابل مشاهده است و فقط اجازه‌ی «افزودن یک ردیف لاگ» را می‌دهد
 *      (خطر: هرزنگاری لاگ). سرور باید /api/LogEvent را بر اساس IP محدود (rate-limit) کند.
 *      اگر Key خالی باشد، خطاهای قبل از ورود فقط در کنسول ثبت می‌شوند.
 */

import axios from "axios";
import "@/api/baseApi"; // baseURL + interceptorها (توکن خودکار)
import store from "@/store";
import { logEventKey, isDev } from "@/config";
import { serverState } from "@/utils/serverHealth";

// کدهای State برای دسته‌بندی در پنل مانیتورینگ
export const LOG_STATE = {
    INFO: 0,
    VUE_ERROR: 1,
    WINDOW_ERROR: 2,
    UNHANDLED_REJECTION: 3,
    BOOTSTRAP: 4,
    NETWORK: 5,
    FCM: 6,
};

const SOURCE_STATE = {
    vue: LOG_STATE.VUE_ERROR,
    "window.error": LOG_STATE.WINDOW_ERROR,
    unhandledrejection: LOG_STATE.UNHANDLED_REJECTION,
    "bootstrap:APP_LOAD": LOG_STATE.BOOTSTRAP,
    "bootstrap:session": LOG_STATE.BOOTSTRAP,
    "fcm.getToken": LOG_STATE.FCM,
};

const SENSITIVE = /token|password|authorization|secret|captcha|cookie/i;
const MAX_MESSAGE = 3500; // ستون Message محدود است
const MIN_INTERVAL_MS = 4000; // حداکثر یک ارسال هر ۴ ثانیه
const DEDUPE_MS = 60000; // پیام یکسان در این بازه دوباره ارسال نشود

let currentUserId = 0;
let lastSentAt = 0;
let sending = false;
const recent = new Map();

function isSignedIn() {
    try {
        return !!store.state.signedIn;
    } catch (e) {
        return false;
    }
}

/** آیا ارسال به سرور ممکن است؟ (کاربر واردشده یا Key ناشناس تنظیم‌شده) */
export function isMonitoringEnabled() {
    return isSignedIn() || !!logEventKey;
}

/** حذف بازگشتی کلیدها/رشته‌های حساس. */
export function scrub(value, depth = 0) {
    if (value == null || depth > 6) return value;
    if (Array.isArray(value)) return value.map((v) => scrub(v, depth + 1));
    if (typeof value === "object") {
        const out = {};
        for (const k of Object.keys(value)) {
            out[k] = SENSITIVE.test(k) ? "[redacted]" : scrub(value[k], depth + 1);
        }
        return out;
    }
    if (typeof value === "string") {
        return value
            .replace(/Bearer\s+[A-Za-z0-9._~+/-]+=*/gi, "Bearer [redacted]")
            .replace(
                /("?(?:access|refresh)?_?token"?\s*[:=]\s*)"?[A-Za-z0-9._-]+"?/gi,
                "$1[redacted]"
            );
    }
    return value;
}

export async function initMonitoring() {
    if (isDev) {
        // eslint-disable-next-line no-console
        console.info(
            "[monitoring] ثبت خطا:",
            "کاربر واردشده → /api/LogEvent/Client" +
                (logEventKey ? " ؛ ناشناس → /api/LogEvent با Key" : " ؛ ناشناس → فقط کنسول")
        );
    }
    return true;
}

export function setMonitoringUser(id) {
    currentUserId = id || 0;
}

function buildMessage(err, context) {
    const c = context || {};
    const parts = [];
    parts.push(`[${c.source || "client"}]`);
    if (currentUserId) parts.push(`user=${currentUserId}`);
    if (c.userId && c.userId !== currentUserId) parts.push(`ctxUser=${c.userId}`);

    if (err instanceof Error) {
        parts.push(`${err.name || "Error"}: ${err.message || ""}`);
        if (err.stack) {
            parts.push(String(err.stack).split("\n").slice(0, 12).join("\n"));
        }
    } else if (typeof err === "string") {
        parts.push(err);
    } else {
        try {
            parts.push(JSON.stringify(scrub(err)));
        } catch (e) {
            parts.push(String(err));
        }
    }

    if (c.info) parts.push(`info: ${c.info}`);
    if (typeof location !== "undefined") {
        parts.push(`url: ${location.pathname}${location.search}`);
    }
    if (typeof navigator !== "undefined") {
        parts.push(`ua: ${navigator.userAgent}`);
    }

    let msg = scrub(parts.join("\n"));
    if (msg.length > MAX_MESSAGE) msg = msg.slice(0, MAX_MESSAGE) + " …";
    return msg;
}

function stateFor(context) {
    const s = context && context.source;
    return (s && SOURCE_STATE[s]) || LOG_STATE.INFO;
}

/**
 * ارسال یک خطا به endpoint داخلی. با throttle و dedupe. هیچ‌وقت throw نمی‌کند.
 */
export function captureError(err, context) {
    // (کنسول در reportClientError چاپ می‌شود)
    // سرور قطع است → خطاهای ناشی از قطعی را به همان سرورِ قطع نفرست.
    if (serverState.down || (err && err.isServerDown)) return Promise.resolve();

    const signedIn = isSignedIn();
    if (!signedIn && !logEventKey) return Promise.resolve();

    const message = buildMessage(err, context);
    const now = Date.now();

    // dedupe پیام یکسان
    const key = message.slice(0, 200);
    const prev = recent.get(key);
    if (prev && now - prev < DEDUPE_MS) return Promise.resolve();
    recent.set(key, now);
    if (recent.size > 50) {
        for (const [k, ts] of recent) {
            if (now - ts > DEDUPE_MS) recent.delete(k);
        }
    }

    // throttle کلی
    if (sending || now - lastSentAt < MIN_INTERVAL_MS) return Promise.resolve();
    sending = true;
    lastSentAt = now;

    const req = signedIn
        ? axios.post("LogEvent/Client", { State: stateFor(context), Message: message })
        : axios.post("LogEvent", {
              State: stateFor(context),
              Key: logEventKey,
              Message: message,
          });

    return req
        .catch(() => {
            /* ثبت خطا نباید خودش خطا تولید کند */
        })
        .finally(() => {
            sending = false;
        });
}

export default {
    LOG_STATE,
    isMonitoringEnabled,
    initMonitoring,
    captureError,
    setMonitoringUser,
    scrub,
};
